import React, { useState, useEffect, useCallback } from 'react';
import Icon from './AppIcon';
import Button from './ui/Button';
import { useAuth } from '../contexts/AuthContext';
import { taskService, dailyTaskService } from '../services/taskService';
import { snagService } from '../services/snagService';
import { incidentService } from '../services/incidentService';
import { siteReportService } from '../services/siteReportService';
import { activityService } from '../services/activityService';
import { getChatCompletion } from '../services/aiIntegrations/chatCompletion';

const SECTION_ICONS = {
  'Work Completed Today': 'CheckCircle2',
  'Issues Identified': 'AlertTriangle',
  'Safety Observations': 'ShieldCheck',
  'Summary of the Day': 'FileText',
};

const SECTION_COLORS = {
  'Work Completed Today': 'text-green-600 bg-green-50 border-green-200',
  'Issues Identified': 'text-amber-600 bg-amber-50 border-amber-200',
  'Safety Observations': 'text-blue-600 bg-blue-50 border-blue-200',
  'Summary of the Day': 'text-purple-600 bg-purple-50 border-purple-200',
};

const parseReportSections = (text) => {
  if (!text) return [];
  const sectionTitles = ['Work Completed Today', 'Issues Identified', 'Safety Observations', 'Summary of the Day'];
  const sections = [];
  sectionTitles?.forEach((title, idx) => {
    const startPattern = new RegExp(`(?:#{1,3}\\s*)?\\*{0,2}${title}\\*{0,2}`, 'i');
    const startMatch = text?.search(startPattern);
    if (startMatch === -1) return;
    const nextTitle = sectionTitles?.[idx + 1];
    let endIdx = text?.length;
    if (nextTitle) {
      const endPattern = new RegExp(`(?:#{1,3}\\s*)?\\*{0,2}${nextTitle}\\*{0,2}`, 'i');
      const endMatch = text?.search(endPattern);
      if (endMatch !== -1) endIdx = endMatch;
    }
    const content = text?.slice(startMatch, endIdx)?.replace(startPattern, '')?.replace(/^[:\s]+/, '')?.trim();
    sections?.push({ title, content });
  });
  return sections?.length > 0 ? sections : [{ title: 'Report', content: text }];
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr + 'T00:00:00')?.toLocaleDateString('en-GB', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });
};

const AIDailyReportModal = ({ isOpen, onClose, siteId, siteName, companyId }) => {
  const { userProfile } = useAuth();
  const today = new Date()?.toISOString()?.split('T')?.[0];

  const [reportDate, setReportDate] = useState(today);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [reportText, setReportText] = useState('');
  const [savedReportId, setSavedReportId] = useState(null);
  const [step, setStep] = useState('confirm'); // 'confirm' | 'generating' | 'result'
  const [toast, setToast] = useState(null);
  const [statusText, setStatusText] = useState('');
  const [errorDetail, setErrorDetail] = useState('');

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setReportDate(today);
      setReportText('');
      setSavedReportId(null);
      setStep('confirm');
      setToast(null);
      setStatusText('');
      setErrorDetail('');
      // Check if a report already exists for today
      checkExistingReport(siteId, today);
    }
  }, [isOpen, siteId, today]);

  const checkExistingReport = async (sid, date) => {
    const result = await siteReportService?.getReportByDate(sid, date);
    if (result?.data) {
      setReportText(result?.data?.report_text);
      setSavedReportId(result?.data?.id);
      setStep('result');
    }
  };

  const buildPrompt = (tasks, snags, incidents) => {
    const taskList = tasks?.slice(0, 50)?.map((t) =>
      `- ${t?.task_name || t?.title || 'Task'} [${t?.status || 'completed'}]${t?.notes ? ` — ${t?.notes}` : ''}`
    )?.join('\n') || 'No tasks recorded.';

    const snagList = snags?.slice(0, 50)?.map((s) =>
      `- ${s?.title || s?.description || 'Snag'} [${s?.status || 'open'}] — ${s?.category || 'General'}`
    )?.join('\n') || 'No snags recorded.';

    const incidentList = incidents?.slice(0, 20)?.map((i) =>
      `- ${i?.title || i?.incident_type || 'Incident'} [${i?.severity || 'low'}]`
    )?.join('\n') || 'No incidents recorded.';

    return `You are a professional construction site report writer. Generate a concise daily site report for ${siteName || 'the site'} on ${reportDate}.

DATA:
Completed/Active Tasks:
${taskList}

Snag Reports:
${snagList}

Incidents:
${incidentList}

Write a professional report with EXACTLY these four sections (use these exact headings):
1. Work Completed Today
2. Issues Identified
3. Safety Observations
4. Summary of the Day

Be concise, factual, and professional. Use bullet points where appropriate. Keep each section to 3-5 sentences or bullet points.`;
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    setStep('generating');
    setReportText('');
    setSavedReportId(null);
    setErrorDetail('');

    try {
      // Step 1: Load site data
      setStatusText('Loading site data...');
      console.log('[AIDailyReportModal] Fetching data for siteId:', siteId);

      const [tasksResult, snagsResult, incidentsResult] = await Promise.all([
        dailyTaskService?.getTasksBySite
          ? dailyTaskService?.getTasksBySite(siteId)
          : taskService?.getTasksBySite?.(siteId),
        snagService?.getSnagsBySite(siteId),
        incidentService?.getIncidentsBySite(siteId),
      ]);

      console.log('[AIDailyReportModal] Data loaded — tasks:', tasksResult?.data?.length, 'snags:', snagsResult?.data?.length, 'incidents:', incidentsResult?.data?.length);

      const tasks = tasksResult?.data || [];
      const snags = snagsResult?.data || [];
      const incidents = incidentsResult?.data || [];

      const dateStr = reportDate;
      const dayTasks = tasks?.filter((t) => {
        const d = t?.created_at || t?.date || t?.scheduled_date;
        return d ? d?.startsWith(dateStr) : true;
      });
      const daySnags = snags?.filter((s) => {
        const d = s?.created_at || s?.date;
        return d ? d?.startsWith(dateStr) : true;
      });
      const dayIncidents = incidents?.filter((i) => {
        const d = i?.incident_date || i?.created_at;
        return d ? d?.startsWith(dateStr) : true;
      });

      const finalTasks = dayTasks?.length > 0 ? dayTasks : tasks?.slice(0, 50);
      const finalSnags = daySnags?.length > 0 ? daySnags : snags?.slice(0, 50);
      const finalIncidents = dayIncidents?.length > 0 ? dayIncidents : incidents?.slice(0, 20);

      console.log('[AIDailyReportModal] Using — tasks:', finalTasks?.length, 'snags:', finalSnags?.length, 'incidents:', finalIncidents?.length);

      const prompt = buildPrompt(finalTasks, finalSnags, finalIncidents);

      // Step 3: Call AI
      setStatusText('Generating report...');
      console.log('[AIDailyReportModal] Calling getChatCompletion provider=OPEN_AI model=gpt-4o-mini');

      const response = await getChatCompletion(
        'OPEN_AI',
        'gpt-4o-mini',
        [{ role: 'user', content: prompt }],
        { max_completion_tokens: 800 }
      );

      console.log('[AIDailyReportModal] AI response:', JSON.stringify(response)?.substring(0, 300));

      const content = response?.choices?.[0]?.message?.content || '';
      if (!content) {
        console.error('[AIDailyReportModal] Empty content. Full response:', response);
        throw new Error('AI returned an empty response. Please try again.');
      }

      setReportText(content);
      setStep('result');
      setStatusText('');
      showToast('Report generated successfully');
    } catch (err) {
      console.error('[AIDailyReportModal] Generation error:', { code: err?.code, statusCode: err?.statusCode, message: err?.message });
      const detail = err?.statusCode === 429
        ? 'AI service is busy. Please wait a moment and try again.'
        : err?.statusCode >= 500
        ? 'AI service is temporarily unavailable. Please try again shortly.'
        : `Report generation failed: ${err?.message ?? 'unknown error'}`;
      setErrorDetail(detail);
      setStatusText('');
      showToast(detail, 'error');
      setStep('error');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!reportText) {
      showToast('Nothing to save', 'error');
      return;
    }
    setIsSaving(true);
    try {
      const result = await siteReportService?.saveReport({
        siteId,
        companyId: companyId || userProfile?.company_id,
        reportDate,
        reportText,
        generatedBy: userProfile?.id,
      });

      if (result?.error) throw result?.error;

      const newReportId = result?.data?.id;
      setSavedReportId(newReportId);

      // Add activity timeline entry
      await activityService?.createActivity({
        companyId: companyId || userProfile?.company_id,
        siteId,
        activityType: 'report_generated',
        title: 'AI Daily Report Generated',
        description: `AI daily site report generated for ${formatDate(reportDate)}`,
        metadata: { report_id: newReportId, report_date: reportDate },
        relatedEntityId: newReportId,
      });

      showToast('Report saved and added to site timeline');
    } catch (err) {
      console.error('[AIDailyReportModal] save report failed:', { code: err?.code, message: err?.message });
      if (err?.code === 'PGRST301' || err?.code === '42501') {
        showToast(`Permission denied: ${err?.message ?? 'unknown'}`, 'error');
      } else {
        showToast(`Failed to save the report: ${err?.message ?? 'unknown error'}`, 'error');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleExportPDF = () => {
    const sections = parseReportSections(reportText);
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Daily Site Report — ${siteName} — ${formatDate(reportDate)}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 32px; color: #111; }
          h1 { font-size: 22px; margin-bottom: 4px; }
          .meta { color: #555; font-size: 13px; margin-bottom: 24px; }
          .section { border: 1px solid #ddd; border-radius: 8px; padding: 16px; margin-bottom: 16px; }
          .section h3 { font-size: 14px; font-weight: 700; margin: 0 0 8px; }
          .section p { font-size: 13px; line-height: 1.6; white-space: pre-wrap; margin: 0; }
          .footer { margin-top: 32px; font-size: 11px; color: #999; border-top: 1px solid #eee; padding-top: 12px; }
        </style>
      </head>
      <body>
        <h1>Daily Site Report</h1>
        <div class="meta">${siteName} &nbsp;|&nbsp; ${formatDate(reportDate)} &nbsp;|&nbsp; Generated by SiteProof AI</div>
        ${sections?.map(s => `<div class="section"><h3>${s?.title}</h3><p>${s?.content || 'No data recorded.'}</p></div>`)?.join('')}
        <div class="footer">Generated by SiteProof &mdash; ${new Date()?.toLocaleString('en-GB')}</div>
      </body>
      </html>
    `;
    printWindow?.document?.write(html);
    printWindow?.document?.close();
    printWindow?.focus();
    setTimeout(() => printWindow?.print(), 500);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-card border border-border rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-9 h-9 bg-accent/10 rounded-lg">
              <Icon name="Sparkles" size={18} color="var(--color-accent)" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">Generate AI Daily Report</h2>
              <p className="text-xs text-muted-foreground">{siteName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        {/* Toast */}
        {toast && (
          <div className={`mx-6 mt-4 flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium flex-shrink-0 ${
            toast?.type === 'error' ?'bg-red-50 border border-red-200 text-red-700' :'bg-green-50 border border-green-200 text-green-700'
          }`}>
            <Icon name={toast?.type === 'error' ? 'AlertCircle' : 'CheckCircle2'} size={15} />
            {toast?.msg}
          </div>
        )}

        {/* Scrollable Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

          {/* Date selector + confirm */}
          {(step === 'confirm' || step === 'result' || step === 'error') && (
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">Report Date</label>
                <input
                  type="date"
                  value={reportDate}
                  max={today}
                  onChange={(e) => {
                    setReportDate(e?.target?.value);
                    setReportText('');
                    setSavedReportId(null);
                    setStep('confirm');
                    setErrorDetail('');
                    checkExistingReport(siteId, e?.target?.value);
                  }}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent transition-colors"
                />
              </div>
              {(step === 'confirm' || step === 'error') && (
                <Button
                  variant="default"
                  onClick={handleGenerate}
                  disabled={isGenerating}
                  className="flex-shrink-0"
                >
                  <Icon name="Sparkles" size={15} />
                  {step === 'error' ? 'Retry' : 'Generate Report'}
                </Button>
              )}
              {step === 'result' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setStep('confirm'); setReportText(''); setSavedReportId(null); setErrorDetail(''); }}
                  className="flex-shrink-0"
                >
                  <Icon name="RefreshCw" size={14} />
                  New Report
                </Button>
              )}
            </div>
          )}

          {/* Generating state */}
          {step === 'generating' && (
            <div className="flex flex-col items-center justify-center py-12 gap-4">
              <div className="relative">
                <div className="w-16 h-16 rounded-full border-4 border-accent/20 border-t-accent animate-spin" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <Icon name="Sparkles" size={20} color="var(--color-accent)" />
                </div>
              </div>
              <div className="text-center">
                <p className="text-sm font-semibold text-foreground">{statusText || 'Generating your report...'}</p>
                <p className="text-xs text-muted-foreground mt-1">Fetching site data and analysing with AI — usually 10–20 seconds</p>
              </div>
            </div>
          )}

          {/* Error state */}
          {step === 'error' && errorDetail && (
            <div className="flex items-start gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
              <Icon name="AlertCircle" size={16} className="flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">Error generating report</p>
                <p className="mt-0.5 text-red-600">{errorDetail}</p>
              </div>
            </div>
          )}

          {/* Result: edit + preview */}
          {step === 'result' && reportText && (
            <>
              {/* Action bar */}
              <div className="flex items-center justify-between gap-2 bg-muted/40 border border-border rounded-xl px-4 py-2.5">
                <div className="flex items-center gap-2">
                  <Icon name="FileCheck" size={16} color="var(--color-accent)" />
                  <span className="text-sm font-medium text-foreground">Report ready</span>
                  {savedReportId && (
                    <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full">
                      <Icon name="CheckCircle2" size={11} />
                      Saved
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={handleExportPDF}>
                    <Icon name="Printer" size={13} />
                    Export PDF
                  </Button>
                  <Button variant="default" size="sm" onClick={handleSave} disabled={isSaving}>
                    {isSaving ? (
                      <><Icon name="Loader2" size={13} className="animate-spin" />Saving...</>
                    ) : (
                      <><Icon name="Save" size={13} />{savedReportId ? 'Update' : 'Save'}</>
                    )}
                  </Button>
                </div>
              </div>

              {/* Editable textarea */}
              <div className="bg-card border border-border rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="PenLine" size={14} color="var(--color-accent)" />
                  <span className="text-xs font-medium text-foreground">Edit Report</span>
                  <span className="text-xs text-muted-foreground">(optional — edit before saving)</span>
                </div>
                <textarea
                  value={reportText}
                  onChange={(e) => setReportText(e?.target?.value)}
                  rows={10}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground font-mono focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent transition-colors resize-y"
                />
              </div>

              {/* Formatted preview */}
              <div className="bg-card border border-border rounded-xl p-5">
                <div className="border-b border-border pb-3 mb-4">
                  <h3 className="text-base font-bold text-foreground">Daily Site Report</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">{siteName} &mdash; {formatDate(reportDate)}</p>
                </div>
                <div className="space-y-3">
                  {parseReportSections(reportText)?.map((section) => (
                    <div
                      key={section?.title}
                      className={`border rounded-lg p-3 ${SECTION_COLORS?.[section?.title] || 'text-foreground bg-muted/30 border-border'}`}
                    >
                      <div className="flex items-center gap-2 mb-1.5">
                        <Icon name={SECTION_ICONS?.[section?.title] || 'FileText'} size={14} />
                        <h4 className="font-semibold text-xs">{section?.title}</h4>
                      </div>
                      <div className="text-xs leading-relaxed whitespace-pre-wrap">
                        {section?.content || <span className="italic opacity-60">No data recorded.</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-border flex-shrink-0 flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            {step === 'confirm' && 'Site data will be fetched automatically'}
            {step === 'generating' && statusText}
            {step === 'result' && 'Save to add to site activity timeline'}
            {step === 'error' && 'Click Retry to try again'}
          </p>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AIDailyReportModal;
